import { TFunction } from "i18next";
import { AppConfigApi } from "src/services/Api/Organization/types";
import { Providers } from "../services/types";

export const getApplications = (t: TFunction): AppConfigApi[] => {
	return [
		{
			provider: Providers.GOPOS,
			name: t("modules.app.field.gopos.title"),
			description: t("modules.app.field.gopos.helptext.description"),
		},
		{
			provider: Providers.GOPOS_V2,
			name: t("modules.app.field.gopos_v2.title"),
			description: t("modules.app.field.gopos_v2.helptext.description"),
		},
		{
			provider: Providers.LOCATIONS,
			name: t("modules.app.field.locations.title"),
			description: t("modules.app.field.locations.helptext.description"),
		},
	];
};

export const parseApplicationSettings = (settings: Record<string, any>): Record<string, any> => {
	return Object.keys(settings).reduce((acc: Record<string, any>, key) => {
		const value = settings[key];
		acc[key] = value;

		if (typeof value === "object" && value?.length >= 0) {
			if (value.some((v: Record<string, any>) => !!v?.slug)) {
				acc[key] = value.map(({ slug }: Record<string, any>) => slug).join(",");
			} else if (value.some((v: Record<string, any>) => !!v?.id)) {
				acc[key] = value.map(({ id }: Record<string, any>) => id).join(",");
			} else {
				acc[key] = value.join(",");
			}
		}
		return acc;
	}, {});
};

const booleanFields = [
	"ACTIVE_SYNC",
	"AUTO_CLOSE_ORDERS",
	"IMPORT_ORDERS",
	"IMPORT_PRODUCTS",
	"AUTO_CREATE_IMPORTED_PRODUCTS",
	"AUTO_LINKED_NAME",
	"AUTO_LINKED_SKU",
	"UPDATE_IMPORTED_PRODUCTS",
	"AUTO_LINK_CHANNEL_LINKEDS_BY_REFERENCE_IDS",
	"EXPORT_PRODUCT_STATES",
	"EXPORT_PRODUCTS_DESCRIPTION",
	"EXPORT_PRODUCT_DATA",
	"SKIP_UPDATE_OF_CRITICAL_AND_OPTIMAL_PRODUCT_STATE",
	"AUTO_CREATE_IMPORTED_PRODUCT_CATEGORIES",
	"UPDATE_IMPORTED_PRODUCT_CATEGORIES",
	"IMPORT_CONTACTS",
	"AUTO_CREATE_IMPORTED_CONTACTS",
	"UPDATE_IMPORTED_CONTACTS",
	"EXPORT_ORDERS",
	"EXPORT_PRODUCT_DATA",
	"IMPORT_ROLES",
	"SYNC_DOCUMENTS_CUSTOM_FIELDS",
];

export const parseApplicationSettingsValues = (settings: Record<string, string>) => {
	return Object.keys(settings).reduce((acc: Record<string, any>, key) => {
		const value = settings[key];

		if (booleanFields.includes(key)) {
			if (value === "false") return { ...acc, [key]: false };
			if (value === "true") return { ...acc, [key]: true };
		}

		return acc;
	}, {});
};

const parseMultiValueToString = (settings: Record<string, any>, field: string): string => {
	if (typeof settings[field] === "object") {
		return settings[field].map((singleField: string) => singleField).join(",");
	}
	return settings[field];
};

export const parseStringToList = (
	settings: Record<string, any>,
	config: Record<string, any>,
	settingsField: string,
	configField: string
): Record<string, any>[] => {
	const parsedValue = parseMultiValueToString(settings, settingsField);
	return parsedValue?.split(",")?.map((x: string) => {
		return config[configField]?.find(
			(configSingleField: Record<string, any>) =>
				(configSingleField.slug ? configSingleField.slug : Number(configSingleField.id)) ===
				(configSingleField.slug ? x : Number(x))
		);
	});
};

export const getAutoCreateProductsTypeOptions = (t: TFunction<"translation", undefined>) => [
	{
		value: "IN_STOCK",
		label: t("enums.apps.auto_create_products_type.IN_STOCK"),
	},
	{
		value: "WITH_PRICE",
		label: t("enums.apps.auto_create_products_type.WITH_PRICE"),
	},
	{
		value: "WITH_PRICE_AND_IN_STOCK",
		label: t("enums.apps.auto_create_products_type.WITH_PRICE_AND_IN_STOCK"),
	},
	{
		value: "DISABLED",
		label: t("enums.apps.auto_create_products_type.DISABLED"),
	},
];

export const getProductExportPriceTypeOptions = (t: TFunction<"translation", undefined>) => [
	{
		value: "SALE_PRICE",
		label: t("enums.apps.product_export_price_type.SALE_PRICE"),
	},
	{
		value: "MAC",
		label: t("enums.apps.product_export_price_type.MAC"),
	},
	{
		value: "REFERENCE_PRICE",
		label: t("enums.apps.product_export_price_type.REFERENCE_PRICE"),
	},
	{
		value: "LAST_PRICE",
		label: t("enums.apps.product_export_price_type.LAST_PRICE"),
	},
	{
		value: "DISABLED",
		label: t("enums.apps.product_export_price_type.DISABLED"),
	},
];

export const getApplicationsHeaderTitle = (pathname: string, t: TFunction) => {
	const pathParts = pathname.split("/");
	const lastPathPart = pathParts[pathParts.length - 1];

	switch (lastPathPart) {
		case "active":
			return t("modules.app.header.installed.title");
		case "all":
			return t("modules.app.field.all.title");
		case "linking":
			return t("modules.app_link.header.title");
		case "to_link":
			return t("modules.app.field.to_link.title");
		case "linking_products":
			return t("modules.app.field.linking_products.title");
		case "linking_contacts":
			return t("modules.app.field.linking_contacts.title");
		case "linking_stocks":
			return t("modules.app.field.linking_stocks.title");
		default:
			return "";
	}
};
